/**
 * Shared collapsed media bar (#2233).
 *
 * Moved from `caption-editor/split-view/collapsed-media-player.tsx`.
 * Used by the caption editor's split view as the bottom transport
 * strip when the video is docked as a floating overlay.
 *
 * The transcript editor has its own richer `TranscriptPlayer` — this
 * component is deliberately narrow and only exposes the controls the
 * split-view flow needs.
 */

import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Pause, Play, Redo2, Undo2 } from 'lucide-react';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { useThemeVars } from '@utils/hooks/use-theme-vars.ts';
import { formatTime } from '@utils/audio.ts';
import { playbackRates } from '@utils/constants/playback-rates.ts';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { PrimaryColor } from '@utils/constants/style.ts';

/* eslint-disable no-restricted-syntax -- Semantic playback colors (primary CTA on tinted bg) must stay stable across themes */

interface CollapsedMediaBarProps {
  /** Grey the bar out when the session has no playable media. */
  hasMedia: boolean;
}

export function CollapsedMediaBar({ hasMedia }: CollapsedMediaBarProps) {
  const { t } = useTranslation();
  const { vars, cls } = useThemeVars();
  const {
    play,
    pause,
    currentTime,
    isPlaying,
    rewind,
    forward,
    playbackRate,
    setPlaybackRate,
    duration,
    setTime
  } = useAudio();

  const progressPercent = useMemo(
    () => (duration > 0 ? (currentTime / duration) * 100 : 0),
    [currentTime, duration]
  );

  // #2294: the bar was a non-interactive progress fill, which meant
  // split-view users had no way to seek — the floating overlay hides
  // the full `CaptionMediaSlider`, and clicking a segment only seeks
  // when linked playback is on. Clicking anywhere on the track now
  // jumps to that spot; dragging continues the scrub.
  const trackRef = useRef<HTMLDivElement | null>(null);
  const seekFromClientX = useCallback(
    (clientX: number) => {
      const el = trackRef.current;
      if (!el || duration <= 0) return;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0) return;
      const ratio = Math.min(
        1,
        Math.max(0, (clientX - rect.left) / rect.width)
      );
      setTime(ratio * duration);
    },
    [duration, setTime]
  );
  const onTrackPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!hasMedia || duration <= 0) return;
      (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
      seekFromClientX(e.clientX);
    },
    [hasMedia, duration, seekFromClientX]
  );
  const onTrackPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.buttons === 0) return; // not dragging
      seekFromClientX(e.clientX);
    },
    [seekFromClientX]
  );
  const onTrackPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const el = e.currentTarget as HTMLDivElement;
      if (el.hasPointerCapture(e.pointerId)) {
        el.releasePointerCapture(e.pointerId);
      }
    },
    []
  );

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-4 py-2 border-t shrink-0',
        cls.border,
        cls.cardBg,
        !hasMedia && 'opacity-50 pointer-events-none'
      )}
      style={vars}
    >
      <div className="flex items-center gap-1.5">
        <Tooltip label={t('rewind')}>
          <button
            onClick={() => rewind(5)}
            className={cn(
              'p-1.5 rounded-2xl transition-colors hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              cls.text
            )}
            aria-label={t('rewind')}
          >
            <Undo2 size={14} aria-hidden="true" />
          </button>
        </Tooltip>

        <button
          onClick={() => (isPlaying ? pause() : play())}
          className="p-2 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          style={{ backgroundColor: PrimaryColor, color: '#fff' }}
          aria-label={isPlaying ? t('pause') : t('play')}
        >
          {isPlaying ? (
            <Pause size={14} fill="currentColor" aria-hidden="true" />
          ) : (
            <Play size={14} fill="currentColor" aria-hidden="true" />
          )}
        </button>

        <Tooltip label={t('forward')}>
          <button
            onClick={() => forward(5)}
            className={cn(
              'p-1.5 rounded-2xl transition-colors hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              cls.text
            )}
            aria-label={t('forward')}
          >
            <Redo2 size={14} aria-hidden="true" />
          </button>
        </Tooltip>
      </div>

      <div className="flex-1 flex items-center gap-2 min-w-0">
        <span
          className={cn(
            'text-[11px] font-mono tabular-nums shrink-0',
            cls.text
          )}
        >
          {formatTime(currentTime)}
        </span>
        <div
          ref={trackRef}
          role="slider"
          aria-label={t('seek')}
          aria-valuemin={0}
          aria-valuemax={Math.max(0, Math.round(duration))}
          aria-valuenow={Math.round(currentTime)}
          tabIndex={hasMedia && duration > 0 ? 0 : -1}
          onPointerDown={onTrackPointerDown}
          onPointerMove={onTrackPointerMove}
          onPointerUp={onTrackPointerUp}
          onPointerCancel={onTrackPointerUp}
          onKeyDown={e => {
            if (!hasMedia || duration <= 0) return;
            if (e.key === 'ArrowLeft') {
              e.preventDefault();
              rewind(5);
            } else if (e.key === 'ArrowRight') {
              e.preventDefault();
              forward(5);
            }
          }}
          className={cn(
            'flex-1 h-1 rounded-full overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            hasMedia && duration > 0 ? 'cursor-pointer' : 'cursor-default'
          )}
          style={{ backgroundColor: PrimaryColor + '30' }}
        >
          <div
            className="h-full rounded-full transition-all duration-200 pointer-events-none"
            style={{
              width: `${progressPercent}%`,
              backgroundColor: PrimaryColor
            }}
          />
        </div>
        <span
          className={cn(
            'text-[11px] font-mono tabular-nums shrink-0',
            cls.text
          )}
        >
          {formatTime(duration)}
        </span>
      </div>

      <label className="flex items-center gap-1">
        <span className="sr-only">{t('speed')}</span>
        <select
          value={playbackRate}
          onChange={e => setPlaybackRate(Number(e.target.value))}
          className={cn(
            'text-xs px-1.5 py-1 rounded-2xl border cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            cls.border,
            cls.text
          )}
          style={{ backgroundColor: 'transparent' }}
          aria-label={t('speed')}
        >
          {playbackRates.map(rate => (
            <option key={rate.value} value={rate.value}>
              {rate.label}x
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

export default CollapsedMediaBar;
